/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { QueryService } from '@map/services/query.service';
import { Store } from '@ngxs/store';
import { DataFormDialogComponent } from '@reports/dataForm/data-form-dialog.component';
import { LoadDataForm } from '@reports/state/data-form.actions';
import { LoadDataFormPayload } from '@reports/state/data-form.model';
import { DataFormState } from '@reports/state/data-form.state';
import { LoggerService } from '@shared/services/logger.service';
import { Observable } from 'rxjs';
import { map, mergeMap, switchMap, take } from 'rxjs/operators';

@Component({
    selector: 'dash-gis-report-edit-button',
    templateUrl: './report-edit-button.component.html',
    imports: [ MatButton, AsyncPipe ]
})
export class ReportEditButtonComponent {
    @Output() public saved = new EventEmitter<void>();

    @Input() public label: string;
    @Input() public color: 'primary' | 'accent' | 'warn' | null;
    @Input() public formName: string;
    @Input() public keys: any[];
    @Input() public selectionLayer: string;

    dataFormNames$: Observable<string[]> = inject(Store).select(DataFormState.names);

    constructor(
        private logger: LoggerService,
        private queryService: QueryService,
        private dialog: MatDialog,
        private store: Store
    ) {}

    public edit() {
        this.queryService
            .getSingleSelection$(this.selectionLayer)
            .pipe(
                take(1),
                map((selection) => {
                    const payload: LoadDataFormPayload = {
                        name: this.formName,
                        initialSelection: selection
                    };
                    if (this.keys) {
                        payload.keys = Array.isArray(this.keys) ? this.keys : [ this.keys ];
                    }
                    return payload;
                }),
                mergeMap((payload) => this.store.dispatch(new LoadDataForm(payload))),
                switchMap(() =>
                    this.dialog.open(DataFormDialogComponent, { data: { name: this.formName } }).afterClosed()
                )
            )
            .subscribe(() => {
                this.saved.emit();
            });
    }

    get authorized$(): Observable<boolean> {
        return this.dataFormNames$.pipe(map((names) => names.findIndex((name) => this.formName === name) !== -1));
    }
}
